/**
 * Stream B — B34: Review Intelligence + AI Response
 *
 * Sentiment trends, AI response drafts (EU AI Act label),
 * platform breakdown, rating distribution.
 * Real apiFetch. No mocks. CSS variables only.
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router";
import { ApiError, apiFetch } from "@/lib/api-client";
import {
  Star,
  MessageSquare,
  Clock,
  Sparkles,
  Send,
  Edit3,
  ThumbsUp,
  ThumbsDown,
  Minus,
  AlertCircle,
  FileImage,
  ShieldCheck,
  ShieldOff,
  ArrowUpRight,
} from "lucide-react";

interface ReviewIntelligence {
  averageRating: number;
  totalReviews: number;
  responseRate: number;
  avgResponseTimeHours: number;
  industryAvgResponseRate: number | null;
  sentimentTrends: Array<{ month: string; positive: number; neutral: number; negative: number }>;
  platforms: Array<{ name: string; rating: number; count: number; responseRate: number }>;
  ratingDistribution: Record<number, number>;
  aiDrafts: Array<{
    reviewId: string;
    reviewerName: string;
    platform: string;
    rating: number;
    reviewText: string;
    draftResponse: string;
    sentiment: "positive" | "neutral" | "negative";
    createdAt: string;
  }>;
  contentCandidates: Array<{
    reviewId: string;
    reviewerName: string;
    platform: string;
    rating: number;
    reviewText: string;
    publishedAt: string;
    hasMatchedPerson: boolean;
    matchedPersonId: string | null;
    hasMarketingConsent: boolean;
    consentRecordId: string | null;
    alreadyExported: boolean;
    canGenerateContent: boolean;
    blockedReason: string | null;
  }>;
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${i <= Math.round(rating) ? "fill-[var(--color-warning)] text-[var(--color-warning)]" : "text-[var(--color-border)]"}`}
        />
      ))}
      <span className="ml-1 text-sm font-medium text-[var(--color-foreground)]">{rating.toFixed(1)}</span>
    </div>
  );
}

const SENTIMENT_STYLES = {
  positive: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
  neutral: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  negative: "bg-[var(--color-error)]/10 text-[var(--color-error)]",
} as const;

function AiResponseCard({
  draft,
  onApprove,
  isApproving,
}: {
  draft: ReviewIntelligence["aiDrafts"][number];
  onApprove: (reviewId: string, response: string) => void;
  isApproving: boolean;
}) {
  const [editedResponse, setEditedResponse] = useState(draft.draftResponse);
  const [isEditing, setIsEditing] = useState(false);

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium text-[var(--color-foreground)]">{draft.reviewerName}</span>
          <span className="text-sm text-[var(--color-muted-foreground)]">on {draft.platform}</span>
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${SENTIMENT_STYLES[draft.sentiment]}`}>
            {draft.sentiment}
          </span>
        </div>
        <StarRating rating={draft.rating} />
      </div>

      <p className="mt-2 text-sm text-[var(--color-muted-foreground)] italic">&ldquo;{draft.reviewText}&rdquo;</p>

      <div className="mt-3 rounded-lg bg-[var(--color-ai)]/5 border border-[var(--color-ai)]/20 p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Sparkles className="h-4 w-4 text-[var(--color-ai)]" />
          <span className="text-xs font-medium text-[var(--color-ai)]">AI-generated response — review before sending</span>
        </div>

        {isEditing ? (
          <textarea
            value={editedResponse}
            onChange={(e) => setEditedResponse(e.target.value)}
            className="w-full rounded border border-[var(--color-border)] bg-[var(--color-background)] p-2 text-sm text-[var(--color-foreground)] resize-none focus:outline-none focus:ring-1 focus:ring-[var(--color-ai)]"
            rows={3}
          />
        ) : (
          <p className="text-sm text-[var(--color-foreground)]">{editedResponse}</p>
        )}

        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => onApprove(draft.reviewId, editedResponse)}
            disabled={isApproving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            <Send className="h-3 w-3" />
            {isApproving ? "Sending..." : "Approve & Send"}
          </button>
          <button
            type="button"
            onClick={() => setIsEditing(!isEditing)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
          >
            <Edit3 className="h-3 w-3" />
            {isEditing ? "Done Editing" : "Edit"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ReviewIntelligence() {
  const [data, setData] = useState<ReviewIntelligence | null>(null);
  const [escalationCounts, setEscalationCounts] = useState<{ total: number; mineCount: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [contentBusyReviewId, setContentBusyReviewId] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: ReviewIntelligence }>("/crm/reviews/intelligence");
      setData(res.data);
    } catch {
      setError(true);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // RV-H: light-weight counter so the operator sees the escalation tile
  // populated without leaving the dashboard. Failure is non-fatal — the
  // tile still renders as a navigation link.
  const fetchEscalationCounts = useCallback(async () => {
    try {
      const res = await apiFetch<{
        data: { items: unknown[]; total: number; mineCount: number };
      }>("/crm/reviews/escalation-queue");
      setEscalationCounts({ total: res.data.total, mineCount: res.data.mineCount });
    } catch {
      setEscalationCounts(null);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchEscalationCounts(); }, [fetchEscalationCounts]);

  const handleApprove = async (reviewId: string, response: string) => {
    setIsApproving(true);
    setApproveError(null);
    try {
      await apiFetch("/crm/reviews/respond", {
        method: "POST",
        body: JSON.stringify({ reviewId, response }),
      });
      await fetchData();
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.message === "Review source not connected") {
          setApproveError("Connect the review provider before sending this response.");
        } else if (error.message === "OAuth tokens expired. Reconnect the review source.") {
          setApproveError("The connected review provider needs to be reconnected before sending.");
        } else if (error.message === "Rate limit exceeded. Try again in a minute.") {
          setApproveError("You have reached the review reply rate limit. Try again in a minute.");
        } else if (error.message === "Review already has a published response") {
          setApproveError("This review has already been responded to from another session.");
        } else {
          setApproveError(error.message);
        }
      } else {
        setApproveError("Failed to send response. Please try again.");
      }
    } finally {
      setIsApproving(false);
    }
  };

  const handleSetContentConsent = async (reviewId: string, granted: boolean) => {
    setContentBusyReviewId(reviewId);
    setContentError(null);
    try {
      await apiFetch(`/crm/reviews/${reviewId}/content-consent`, {
        method: "PUT",
        body: JSON.stringify({ granted }),
      });
      await fetchData();
    } catch (error) {
      if (error instanceof ApiError) {
        setContentError(error.message);
      } else {
        setContentError("Failed to update review reuse consent. Please try again.");
      }
    } finally {
      setContentBusyReviewId(null);
    }
  };

  const handleCreateContent = async (reviewId: string) => {
    setContentBusyReviewId(reviewId);
    setContentError(null);
    try {
      await apiFetch(`/crm/reviews/${reviewId}/to-content`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      await fetchData();
    } catch (error) {
      if (error instanceof ApiError) {
        setContentError(error.message);
      } else {
        setContentError("Failed to turn this review into content. Please try again.");
      }
    } finally {
      setContentBusyReviewId(null);
    }
  };

  const industryBenchmarkLabel = data?.industryAvgResponseRate == null
    ? "Industry avg unavailable"
    : `Industry avg: ${data.industryAvgResponseRate}%`;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 animate-pulse rounded bg-[var(--color-surface)]" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-[var(--color-surface)]" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 p-4 text-sm text-[var(--color-error)]">
        <AlertCircle className="h-4 w-4" />
        Failed to load review intelligence. Please try again.
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center">
        <Star className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)] mb-3" />
        <p className="font-medium text-[var(--color-foreground)]">No reviews yet</p>
        <p className="text-sm text-[var(--color-muted-foreground)] mt-1">Connect your review sources (Google, Facebook, Yelp) to monitor and respond with AI.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Review Intelligence</h1>
        <span className="text-sm text-[var(--color-muted-foreground)]">{data.totalReviews} total reviews</span>
      </div>

      {/* Top metrics */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
            <Star className="h-4 w-4" /> Average Rating
          </div>
          <div className="mt-2"><StarRating rating={data.averageRating} /></div>
        </div>
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
            <MessageSquare className="h-4 w-4" /> Response Rate
          </div>
          <div className="mt-2 text-2xl font-bold text-[var(--color-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>{data.responseRate}%</div>
          <div className="text-xs text-[var(--color-muted-foreground)]">{industryBenchmarkLabel}</div>
        </div>
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
            <Clock className="h-4 w-4" /> Avg Response Time
          </div>
          <div className="mt-2 text-2xl font-bold text-[var(--color-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>{data.avgResponseTimeHours}h</div>
          <div className="text-xs text-[var(--color-muted-foreground)]">Target: under 4h</div>
        </div>
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
            <Sparkles className="h-4 w-4 text-[var(--color-ai)]" /> AI Drafts Ready
          </div>
          <div className="mt-2 text-2xl font-bold text-[var(--color-ai)]">{data.aiDrafts.length}</div>
          <div className="text-xs text-[var(--color-muted-foreground)]">Awaiting your approval</div>
        </div>
      </div>

      {/* RV-H: Escalation queue tile — links to the operator queue page */}
      <Link
        to="/reputation/escalations"
        className="block rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 hover:bg-[var(--color-surface)]"
        aria-label="Open escalation queue"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
              <AlertCircle className="h-4 w-4 text-[var(--color-error)]" /> Escalation Queue
            </div>
            <div className="mt-2 flex items-baseline gap-3">
              <span
                className="text-2xl font-bold text-[var(--color-foreground)]"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {escalationCounts?.mineCount ?? 0}
              </span>
              <span className="text-sm text-[var(--color-muted-foreground)]">
                assigned to you
              </span>
              <span className="text-sm text-[var(--color-muted-foreground)]">
                / {escalationCounts?.total ?? 0} team total
              </span>
            </div>
          </div>
          <ArrowUpRight className="h-4 w-4 text-[var(--color-muted-foreground)]" />
        </div>
      </Link>

      {/* Sentiment Trends */}
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
        <h2 className="text-lg font-medium text-[var(--color-foreground)] mb-4">Sentiment Trends</h2>
        <div className="space-y-3">
          {data.sentimentTrends.map((month) => {
            const total = month.positive + month.neutral + month.negative;
            const posPct = total > 0 ? (month.positive / total) * 100 : 0;
            const neuPct = total > 0 ? (month.neutral / total) * 100 : 0;
            return (
              <div key={month.month} className="flex items-center gap-3">
                <span className="w-16 text-sm text-[var(--color-muted-foreground)]">{month.month}</span>
                <div className="flex-1 flex h-5 rounded-full overflow-hidden">
                  <div className="bg-[var(--color-success)] transition-all" style={{ width: `${posPct}%` }} />
                  <div className="bg-[var(--color-warning)] transition-all" style={{ width: `${neuPct}%` }} />
                  <div className="bg-[var(--color-error)] transition-all" style={{ width: `${100 - posPct - neuPct}%` }} />
                </div>
                <span className="w-12 text-right text-xs text-[var(--color-muted-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>{total}</span>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex items-center gap-4 text-xs text-[var(--color-muted-foreground)]">
          <span className="flex items-center gap-1"><ThumbsUp className="h-3 w-3 text-[var(--color-success)]" /> Positive</span>
          <span className="flex items-center gap-1"><Minus className="h-3 w-3 text-[var(--color-warning)]" /> Neutral</span>
          <span className="flex items-center gap-1"><ThumbsDown className="h-3 w-3 text-[var(--color-error)]" /> Negative</span>
        </div>
      </div>

      {/* Rating Distribution */}
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
        <h2 className="text-lg font-medium text-[var(--color-foreground)] mb-4">Rating Distribution</h2>
        <div className="space-y-2">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = data.ratingDistribution[star] ?? 0;
            const pct = data.totalReviews > 0 ? (count / data.totalReviews) * 100 : 0;
            const barColor = star >= 4 ? "var(--color-success)" : star === 3 ? "var(--color-warning)" : "var(--color-error)";
            return (
              <div key={star} className="flex items-center gap-3">
                <span className="w-8 text-sm text-[var(--color-muted-foreground)]">{star}</span>
                <Star className="h-3.5 w-3.5 text-[var(--color-warning)]" />
                <div className="flex-1 h-3 rounded-full bg-[var(--color-surface)] overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                </div>
                <span className="w-12 text-right text-xs text-[var(--color-muted-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {approveError && (
        <div className="rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-3 text-sm text-[var(--color-error)]">
          {approveError}
        </div>
      )}

      {contentError && (
        <div className="rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-3 text-sm text-[var(--color-error)]">
          {contentError}
        </div>
      )}

      {data.contentCandidates.length > 0 && (
        <div>
          <h2 className="text-lg font-medium text-[var(--color-foreground)] mb-4 flex items-center gap-2">
            <FileImage className="h-5 w-5 text-[var(--color-primary)]" />
            Content Opportunities
          </h2>
          <div className="space-y-4">
            {data.contentCandidates.map((candidate) => {
              const isBusy = contentBusyReviewId === candidate.reviewId;
              return (
                <div key={candidate.reviewId} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[var(--color-foreground)]">{candidate.reviewerName}</span>
                        <span className="text-sm text-[var(--color-muted-foreground)]">on {candidate.platform}</span>
                      </div>
                      <p className="mt-2 text-sm text-[var(--color-muted-foreground)] italic">&ldquo;{candidate.reviewText}&rdquo;</p>
                    </div>
                    <StarRating rating={candidate.rating} />
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                    <span className={`rounded-full px-2.5 py-1 font-medium ${candidate.hasMatchedPerson ? "bg-[var(--color-success)]/10 text-[var(--color-success)]" : "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"}`}>
                      {candidate.hasMatchedPerson ? "CRM contact matched" : "CRM contact match required"}
                    </span>
                    <span className={`rounded-full px-2.5 py-1 font-medium ${candidate.hasMarketingConsent ? "bg-[var(--color-success)]/10 text-[var(--color-success)]" : "bg-[var(--color-error)]/10 text-[var(--color-error)]"}`}>
                      {candidate.hasMarketingConsent ? "Reuse consent recorded" : "Reuse consent missing"}
                    </span>
                    {candidate.alreadyExported && (
                      <span className="rounded-full bg-[var(--color-ai)]/10 px-2.5 py-1 font-medium text-[var(--color-ai)]">
                        Content already created
                      </span>
                    )}
                  </div>

                  {candidate.blockedReason && (
                    <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">{candidate.blockedReason}</p>
                  )}

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleSetContentConsent(candidate.reviewId, !candidate.hasMarketingConsent)}
                      disabled={isBusy || !candidate.hasMatchedPerson}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:opacity-50"
                    >
                      {candidate.hasMarketingConsent ? <ShieldOff className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                      {candidate.hasMarketingConsent ? "Revoke reuse consent" : "Record reuse consent"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCreateContent(candidate.reviewId)}
                      disabled={isBusy || !candidate.canGenerateContent}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
                    >
                      <FileImage className="h-3.5 w-3.5" />
                      {isBusy ? "Creating..." : candidate.alreadyExported ? "Content created" : "Turn into content"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AI Response Drafts */}
      {data.aiDrafts.length > 0 && (
        <div>
          <h2 className="text-lg font-medium text-[var(--color-foreground)] mb-4 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[var(--color-ai)]" />
            AI Response Drafts
          </h2>
          <div className="space-y-4">
            {data.aiDrafts.map((draft) => (
              <AiResponseCard key={draft.reviewId} draft={draft} onApprove={handleApprove} isApproving={isApproving} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
